import type { FC, ReactNode } from 'react'
import type { Patient, Prescription } from '@/lib/clinic/types'
import type { FilterOption } from '@/components/common/filter-select'

import Link from 'next/link'
import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { ChevronLeft, ChevronRight, Download, EllipsisVertical, Eye, FilePlus2, FileText, Pencil, Printer, Trash2 } from 'lucide-react'
import { useClinic } from '@/hooks/use-clinic'
import { useClinicUi } from '@/hooks/use-clinic-ui'
import { matchesText } from '@/lib/clinic/search'
import { diffDays, formatDate, formatDateTime, formatTime, toDayKey } from '@/lib/clinic/datetime'
import { pluralize } from '@/lib/clinic/format'
import { Button } from '@/components/ui/button'
import { Seo } from '@/components/generals/seo'
import { PATIENTS_PAGE_SIZE } from '@/constants/clinic'
import SearchInput from '@/components/common/search-input'
import FilterSelect from '@/components/common/filter-select'
import PatientAvatar from '@/components/common/patient-avatar'
import PageHeader from '@/components/generals/page-header'
import PageLoader from '@/components/generals/page-loader'
import EmptyState from '@/components/generals/empty-state'
import PageContainer from '@/components/generals/page-container'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table'

type DateFilter = 'all' | 'today' | 'week' | 'month' | 'year'

type SortOption = 'newest' | 'oldest' | 'patient'

interface PrescriptionRow {
    prescription: Prescription
    patient: Patient
}

const DATE_OPTIONS: FilterOption<DateFilter>[] = [
    { value: 'all', label: 'All dates' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'Last 7 days' },
    { value: 'month', label: 'Last 30 days' },
    { value: 'year', label: 'Last 12 months' }
]

const SORT_OPTIONS: FilterOption<SortOption>[] = [
    { value: 'newest', label: 'Newest first' },
    { value: 'oldest', label: 'Oldest first' },
    { value: 'patient', label: 'Patient name A to Z' }
]

const DATE_LIMITS: Record<Exclude<DateFilter, 'all'>, number> = {
    today: 0,
    week: 6,
    month: 29,
    year: 364
}

interface RowActionsProps {
    prescription: Prescription
}

const RowActions: FC<RowActionsProps> = ({ prescription }): ReactNode => {
    const router = useRouter()
    const ui = useClinicUi()

    return (
        <div className='flex items-center justify-end gap-1'>
            <Button size='icon-sm' variant='outline' aria-label={`Print ${prescription.number}`} onClick={() => ui.printPrescription(prescription.id)}>
                <Printer />
            </Button>

            <Button size='icon-sm' variant='outline' aria-label={`Download ${prescription.number} as PDF`} onClick={() => void ui.downloadPrescriptionPdf(prescription.id)}>
                <Download />
            </Button>

            <DropdownMenu>
                <DropdownMenuTrigger render={<Button variant='ghost' size='icon-sm' aria-label={`More actions for ${prescription.number}`} />}>
                    <EllipsisVertical />
                </DropdownMenuTrigger>

                <DropdownMenuContent align='end' className='w-52'>
                    <DropdownMenuItem onClick={() => void router.push(`/prescriptions/${prescription.id}`)}>
                        <Eye />
                        Preview
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={() => void router.push(`/prescriptions/${prescription.id}/edit`)}>
                        <Pencil />
                        Edit prescription
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    <DropdownMenuItem variant='destructive' onClick={() => void ui.deletePrescription(prescription.id)}>
                        <Trash2 />
                        Delete prescription
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    )
}

const PrescriptionsPage: FC = (): ReactNode => {
    const { ready, data, index, today } = useClinic()
    const ui = useClinicUi()

    const [query, setQuery] = useState('')
    const [dateFilter, setDateFilter] = useState<DateFilter>('all')
    const [sort, setSort] = useState<SortOption>('newest')
    const [page, setPage] = useState(1)

    useEffect(() => {
        setPage(1)
    }, [query, dateFilter, sort])

    const rows = useMemo(() => {
        const list: PrescriptionRow[] = []

        for (const prescription of data.prescriptions) {
            const patient = index.patientsById.get(prescription.patientId)
            if (!patient) continue

            if (dateFilter !== 'all') {
                const age = diffDays(toDayKey(prescription.prescribedAt), today)
                if (age < 0 || age > DATE_LIMITS[dateFilter]) continue
            }

            if (query.trim() && !matchesText(query, [
                prescription.number,
                patient.name,
                patient.code,
                patient.phone,
                prescription.diagnosis,
                prescription.complaint,
                ...prescription.medicines.map(medicine => medicine.name)
            ])) continue

            list.push({ prescription, patient })
        }

        return list.sort((a, b) => {
            if (sort === 'patient') return a.patient.name.localeCompare(b.patient.name) || b.prescription.prescribedAt.localeCompare(a.prescription.prescribedAt)
            if (sort === 'oldest') return a.prescription.prescribedAt.localeCompare(b.prescription.prescribedAt)

            return b.prescription.prescribedAt.localeCompare(a.prescription.prescribedAt)
        })
    }, [data.prescriptions, dateFilter, index.patientsById, query, sort, today])

    if (!ready) return <PageLoader />

    const pageCount = Math.max(1, Math.ceil(rows.length / PATIENTS_PAGE_SIZE))
    const currentPage = Math.min(page, pageCount)
    const pageRows = rows.slice((currentPage - 1) * PATIENTS_PAGE_SIZE, currentPage * PATIENTS_PAGE_SIZE)
    const filtersActive = Boolean(query.trim()) || dateFilter !== 'all'

    return (
        <>
            <Seo title='Prescriptions' description='Create, print and download prescriptions for Dr. Sayuja Clinic patients.' />

            <PageContainer>
                <PageHeader
                    title='Prescriptions'
                    description='Every prescription is linked to its patient and consultation, ready to print again at any time.'
                    actions={(
                        <Button size='lg' onClick={() => ui.openPatientPicker('prescription')}>
                            <FilePlus2 />
                            New prescription
                        </Button>
                    )}
                />

                <div className='mb-4 flex flex-col gap-3 lg:flex-row lg:items-center'>
                    <SearchInput
                        id='prescriptions-search'
                        value={query}
                        onChange={setQuery}
                        placeholder='Search by patient, Rx number, diagnosis or medicine'
                        label='Search prescriptions'
                        className='flex-1'
                    />

                    <div className='grid grid-cols-2 gap-2 sm:flex'>
                        <FilterSelect value={dateFilter} onChange={setDateFilter} options={DATE_OPTIONS} label='Filter by date' className='w-full sm:w-44' />
                        <FilterSelect value={sort} onChange={setSort} options={SORT_OPTIONS} label='Sort prescriptions' className='w-full sm:w-52' />
                    </div>
                </div>

                <div className='mb-3 flex items-center justify-between gap-2 text-sm text-muted-foreground'>
                    <p aria-live='polite'>
                        {filtersActive ? `${pluralize(rows.length, 'prescription')} found` : `${pluralize(rows.length, 'prescription')} in total`}
                    </p>

                    {filtersActive && (
                        <Button
                            variant='link'
                            size='sm'
                            className='h-auto px-0'
                            onClick={() => {
                                setQuery('')
                                setDateFilter('all')
                            }}
                        >
                            Clear filters
                        </Button>
                    )}
                </div>

                {rows.length === 0 ? (
                    <EmptyState
                        icon={FileText}
                        title={data.prescriptions.length === 0 ? 'No prescriptions yet' : 'No prescriptions match these filters'}
                        description={data.prescriptions.length === 0
                            ? 'Prescriptions created after a consultation appear here.'
                            : 'Try another search or a wider date range.'}
                        action={(
                            <Button onClick={() => ui.openPatientPicker('prescription')}>
                                <FilePlus2 />
                                New prescription
                            </Button>
                        )}
                    />
                ) : (
                    <div className='overflow-hidden rounded-2xl border border-border bg-card shadow-xs'>
                        <div className='hidden lg:block'>
                            <Table>
                                <TableHeader className='bg-muted/50'>
                                    <TableRow className='hover:bg-transparent'>
                                        <TableHead className='pl-5 text-xs font-medium text-muted-foreground'>Rx No.</TableHead>
                                        <TableHead className='text-xs font-medium text-muted-foreground'>Date and time</TableHead>
                                        <TableHead className='text-xs font-medium text-muted-foreground'>Patient</TableHead>
                                        <TableHead className='text-xs font-medium text-muted-foreground'>Diagnosis</TableHead>
                                        <TableHead className='text-xs font-medium text-muted-foreground'>Medicines</TableHead>
                                        <TableHead className='text-xs font-medium text-muted-foreground'>Consultation</TableHead>
                                        <TableHead className='pr-5 text-right'>
                                            <span className='sr-only'>Actions</span>
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>

                                <TableBody>
                                    {pageRows.map(({ prescription, patient }) => {
                                        const consultation = prescription.consultationId ? index.consultationsById.get(prescription.consultationId) : undefined

                                        return (
                                            <TableRow key={prescription.id}>
                                                <TableCell className='pl-5 align-top'>
                                                    <Link href={`/prescriptions/${prescription.id}`} className='font-semibold text-foreground tabular-nums hover:underline'>
                                                        {prescription.number}
                                                    </Link>
                                                </TableCell>

                                                <TableCell className='align-top tabular-nums'>
                                                    <span className='flex flex-col'>
                                                        <span className='text-foreground'>{formatDate(prescription.prescribedAt)}</span>
                                                        <span className='text-xs text-muted-foreground'>{formatTime(prescription.prescribedAt)}</span>
                                                    </span>
                                                </TableCell>

                                                <TableCell className='align-top'>
                                                    <Link href={`/patients/${patient.code}`} className='flex items-center gap-2.5'>
                                                        <PatientAvatar name={patient.name} seed={patient.id} size='sm' />

                                                        <span className='flex flex-col'>
                                                            <span className='font-medium text-foreground hover:underline'>{patient.name}</span>
                                                            <span className='text-xs text-muted-foreground tabular-nums'>{patient.code}</span>
                                                        </span>
                                                    </Link>
                                                </TableCell>

                                                <TableCell className='max-w-[220px] align-top whitespace-normal'>
                                                    <span className='line-clamp-2 text-sm'>{prescription.diagnosis || '—'}</span>
                                                </TableCell>

                                                <TableCell className='max-w-[240px] align-top whitespace-normal'>
                                                    <span className='line-clamp-2 text-sm text-foreground'>
                                                        {prescription.medicines.map(medicine => medicine.name).join(', ') || 'Advice only'}
                                                    </span>

                                                    <span className='text-xs text-muted-foreground'>
                                                        {pluralize(prescription.medicines.length, 'medicine')}
                                                    </span>
                                                </TableCell>

                                                <TableCell className='align-top text-sm text-muted-foreground tabular-nums'>
                                                    {consultation ? formatDate(consultation.consultedAt) : 'Not linked'}
                                                </TableCell>

                                                <TableCell className='pr-5 align-top'>
                                                    <RowActions prescription={prescription} />
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        </div>

                        <ul className='divide-y divide-border lg:hidden'>
                            {pageRows.map(({ prescription, patient }) => (
                                <li key={prescription.id} className='flex flex-col gap-2 px-4 py-3.5'>
                                    <div className='flex items-start justify-between gap-3'>
                                        <Link href={`/prescriptions/${prescription.id}`} className='flex min-w-0 items-start gap-3'>
                                            <PatientAvatar name={patient.name} seed={patient.id} size='sm' />

                                            <span className='flex min-w-0 flex-col'>
                                                <span className='truncate text-sm font-semibold text-foreground'>
                                                    {patient.name}
                                                </span>

                                                <span className='text-xs text-muted-foreground tabular-nums'>
                                                    {prescription.number} · {formatDateTime(prescription.prescribedAt)}
                                                </span>
                                            </span>
                                        </Link>

                                        <RowActions prescription={prescription} />
                                    </div>

                                    <p className='line-clamp-2 pl-11 text-xs text-muted-foreground'>
                                        {prescription.diagnosis ? `${prescription.diagnosis} · ` : ''}
                                        {prescription.medicines.map(medicine => medicine.name).join(', ') || 'Advice only'}
                                    </p>
                                </li>
                            ))}
                        </ul>

                        {pageCount > 1 && (
                            <div className='flex items-center justify-between gap-2 border-t border-border px-4 py-3 text-sm sm:px-5'>
                                <p className='text-muted-foreground tabular-nums'>
                                    {(currentPage - 1) * PATIENTS_PAGE_SIZE + 1} to {Math.min(currentPage * PATIENTS_PAGE_SIZE, rows.length)} of {rows.length}
                                </p>

                                <div className='flex items-center gap-1.5'>
                                    <Button variant='outline' size='sm' disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>
                                        <ChevronLeft />
                                        Previous
                                    </Button>

                                    <Button variant='outline' size='sm' disabled={currentPage === pageCount} onClick={() => setPage(currentPage + 1)}>
                                        Next
                                        <ChevronRight />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </PageContainer>
        </>
    )
}

export default PrescriptionsPage
